import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  updateMember,
  clearAvatarError,
  updateAvatarUrl,
  MembersReviewState,
  getAvatarUrl
} from 'slices/NdisSlices/platformAdmin/MembersReviewFormSlice';
import { fetchGroups } from 'slices/NdisSlices/platformAdmin/PlatformAdminSlice';
import { fetchRoles } from 'slices/NdisSlices/platformAdmin/ManageRolesSlice';
import { RootState, AppDispatch } from 'store';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Edit from '@mui/icons-material/Edit';
import Cancel from '@mui/icons-material/Cancel';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
  Transgender as TransgenderIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Groups as GroupsIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  CameraAlt
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { styled } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import { fetchUserById, selectProfileSettings } from 'slices/ProfileSettingsSlice';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

// Styled components
const DetailCard = styled(Card)(({ theme }: { theme: Theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4]
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1)
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2)
  }
}));

const DetailSection = styled(Box)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1)
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2)
  }
}));

const SectionTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '0.75rem',
  letterSpacing: '0.5px',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1)
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(2)
  }
}));

// Define FormData interface to match MembersReviewFormSlice
interface FormData {
  id?: string;
  name: string;
  username?: string;
  role?: { role_id: string; label: string }[];
  gender?: string;
  phone: string;
  email: string;
  dob: Date;
  address?: string;
  projects?: string[];
  groups?: string[];
  avartar?: string;
  creation_date_time?: string;
  created_by?: string;
  department?: string;
  status: 'active' | 'inactive' | 'verify pending' | 'approval pending' | 'rejected';
  isActive?: boolean;
}

// Define Yup schema to match FormData interface
const schema: yup.ObjectSchema<FormData> = yup.object().shape({
  id: yup.string().optional(),
  name: yup.string().required('Full Name is required'),
  username: yup.string().optional(),
  role: yup
    .array()
    .of(
      yup.object().shape({
        role_id: yup.string().required(),
        label: yup.string().required()
      })
    )
    .optional(),
  gender: yup.string().optional(),
  phone: yup.string().required('Phone is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  dob: yup.date().required('Date of Birth is required').typeError('Invalid date'),
  address: yup.string().optional(),
  projects: yup.array().of(yup.string().defined()).optional(),
  groups: yup.array().of(yup.string().defined()).optional(),
  avartar: yup.string().optional(),
  creation_date_time: yup.string().optional(),
  created_by: yup.string().optional(),
  department: yup.string().optional(),
  status: yup
    .string()
    .oneOf(['active', 'inactive', 'verify pending', 'approval pending', 'rejected'])
    .required('Status is required'),
  isActive: yup.boolean().optional()
});

const defaultFormValues: FormData = {
  id: '',
  name: '',
  username: '',
  role: [],
  gender: '',
  phone: '',
  email: '',
  dob: new Date(),
  address: '',
  projects: [],
  groups: [],
  avartar: '',
  creation_date_time: '',
  created_by: '',
  department: '',
  status: 'active',
  isActive: true
};

// DetailItem component for label-value pairs
interface DetailItemProps {
  label: string;
  value?: string;
  icon?: React.ReactNode;
  multiline?: boolean;
  children?: React.ReactNode;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value, icon, multiline, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      mb: 1,
      padding: 1,
      borderRadius: 1,
      '&:hover': {
        backgroundColor: 'action.hover'
      }
    }}
  >
    <Box
      sx={{
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        minWidth: 24
      }}
    >
      {icon || <InfoIcon fontSize="small" />}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      {children ? (
        children
      ) : (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.5
          }}
        >
          {value || 'N/A'}
        </Typography>
      )}
    </Box>
  </Box>
);

const ProfileContent: React.FC = () => {
  const [isActive, setIsActive] = useState(true);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'success'>('error');
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState<FormData | null>(null);
  const [existingAvatarBlob, setExistingAvatarBlob] = useState<File | null>(null);
  const avatarBlobUrls = useRef<{ [id: string]: string }>({});

  const dispatch = useDispatch<AppDispatch>();
  const { user, loading, error } = useSelector(selectProfileSettings);
  const { avatarLoading, avatarError, avatarUrls } = useSelector(
    (state: RootState) => state.membersReview as MembersReviewState
  );
  const groups = useSelector((state: RootState) => state.platformAdmin?.groups || []);
  const { roles } = useSelector((state: RootState) => state.manageRoles);

  const selectedProjectId = useSelector(
    (state: RootState) => (state as any).homeTilesServices?.selectedProjectId || ''
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => (state as any).serviceSelected?.selectedServiceTitle || ''
  );
  const userDetails = localStorage.getItem('user-details');
  const loginDetails = userDetails ? JSON.parse(userDetails) : {};
  const loggedInWorkerName = loginDetails.name || '';
  const userId = loginDetails.userId || '';

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: defaultFormValues
  });

  // Map API user data to FormData
  const mapUserToFormData = (user: any): FormData => ({
    id: user.id || '',
    name: user.user_details?.name || '',
    username: user.username || '',
    role: user.user_details?.role || [],
    gender: user.gender || '',
    phone: user.phone || '',
    email: user.user_details?.email || '',
    dob: user.dob ? new Date(user.dob) : new Date(),
    address: user.address || '',
    projects: [],
    groups: user.groups?.map((g: any) => g.group_id) || [],
    avartar: user.avatar || '',
    creation_date_time: user.approved_on || '',
    created_by: user.approved_by || '',
    department: user.department || '',
    status: user.isActive ? 'active' : 'inactive',
    isActive: user.isActive ?? true
  });

  // Fetch user data, groups, and roles on mount
  useEffect(() => {
    console.log(`Fetching profile data for userId: ${userId}`);
    dispatch(fetchUserById(userId)).then((action) => {
      if (fetchUserById.fulfilled.match(action)) {
        console.log('Profile data fetched successfully:', action.payload);
        const formData = mapUserToFormData(action.payload);
        setProfileData(formData);
        setIsActive(formData.isActive ?? formData.status === 'active');
        reset(formData);
      } else if (fetchUserById.rejected.match(action)) {
        console.error('Failed to fetch profile data:', action.payload);
        setSnackbarMessage(action.payload || 'Failed to fetch profile data');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    });
    dispatch(fetchGroups());
    dispatch(fetchRoles());
  }, [dispatch, reset]);

  // Fetch avatar and convert to blob if available
  useEffect(() => {
    console.log('profileData', profileData);
    const fetchAvatar = async () => {
      if (profileData?.id) {
        console.log(`Fetching avatar for user ${profileData.id}: ${profileData.avartar}`);
        try {
          const url = await getAvatarUrl(profileData.avartar || '');
          dispatch(updateAvatarUrl({ id: profileData.id, url }));
          avatarBlobUrls.current[profileData.id] = url;

          const response = await fetch(url);
          if (!response.ok) throw new Error('Failed to fetch avatar image');
          const blob = await response.blob();
          const fileName = profileData?.avartar
            ? profileData.avartar.split('/').pop()
            : 'image.png';

          const file = new File([blob], fileName || '', { type: blob.type });
          setExistingAvatarBlob(file);
          console.log(`Existing avatar fetched as blob: ${fileName}`);
        } catch (err) {
          console.error(`Failed to fetch avatar for user ${profileData.id}:`, err);
          setSnackbarMessage('Failed to load avatar');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
    };
    fetchAvatar();
  }, [profileData]);

  // Update avatar preview when avatarFile changes
  useEffect(() => {
    if (avatarFile) {
      const previewUrl = URL.createObjectURL(avatarFile);
      setAvatarPreview(previewUrl);
      return () => URL.revokeObjectURL(previewUrl);
    } else {
      setAvatarPreview(null);
    }
  }, [avatarFile]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(avatarBlobUrls.current).forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      avatarBlobUrls.current = {};
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // Handle errors
  useEffect(() => {
    if (error || avatarError) {
      setSnackbarMessage(error || avatarError || 'An error occurred');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [error, avatarError]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    dispatch(clearAvatarError());
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(profileData || defaultFormValues);
    setAvatarFile(null);
    setAvatarPreview(null);
    setIsActive(profileData?.isActive ?? profileData?.status === 'active');
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log(`Avatar file selected: ${file.name}`);
      setAvatarFile(file);
      const { email } = getValues();
      if (email) {
        const extension = file.name.split('.').pop() || 'png';
        const avatarPath = `Users/ApprovedMembers/${email
          .replace('@', '_')
          .replace('.', '_')}/image.${extension}`;
        setValue('avartar', avatarPath);
        console.log(`Set avatar path: ${avatarPath}`);
      }
    }
  };

  const handleAvatarRemove = () => {
    setValue('avartar', '');
    setAvatarFile(null);
    setAvatarPreview(null);
    setExistingAvatarBlob(null);
    console.log('Avatar removed');
  };

  const handleAvatarError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.onerror = null;
    target.src = '/default-avatar.png';
    console.log(`Avatar image failed to load, using default`);
  }, []);

  const takePicture = async () => { // For capacitor camera - Android only
    try {
      const image = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera
      });

      if (image.dataUrl) {
        // Convert base64 to File object
        const response = await fetch(image.dataUrl);
        const blob = await response.blob();
        const file = new File([blob], `avatar_${Date.now()}.jpeg`, { type: 'image/jpeg' });

        setAvatarFile(file);
        setAvatarPreview(image.dataUrl);

        // Set avatar path similar to handleAvatarUpload
        const { email } = getValues();
        if (email) {
          const avatarPath = `Users/ApprovedMembers/${email
            .replace('@', '_')
            .replace('.', '_')}/image.jpeg`;
          setValue('avartar', avatarPath);
        }
      }
    } catch (error) {
      console.error('Camera error:', error);
    }
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    console.log('Submitting form data:', data);
    const finalData = {
      ...data,
      avartar: data.avartar || profileData?.avartar || ''
    };
    const avatarToSend = avatarFile || existingAvatarBlob;
    if (!avatarToSend && finalData.avartar) {
      console.warn('No avatar file available, but avatar path exists:', finalData.avartar);
      setSnackbarMessage('Please select an avatar image or remove the existing one');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const payload = {
      data: finalData,
      selectedProjectId,
      selectedServiceTitle,
      loggedInWorkerName,
      componentParentId: '/settings',
      user_whoRequested_id: userId,
      avatarFile: avatarToSend
    };
    console.log('Update payload:', {
      ...payload,
      avatarFile: avatarToSend ? avatarToSend.name : null
    });
    try {
      const result = await dispatch(updateMember(payload)).unwrap();
      console.log('Profile updated successfully:', result);
      setProfileData(result);
      reset(result);
      setSnackbarMessage('Profile updated successfully');
      setSnackbarSeverity('success');
      if (avatarFile && avatarPreview && profileData?.id) {
        dispatch(updateAvatarUrl({ id: profileData.id, url: avatarPreview }));
        avatarBlobUrls.current[profileData.id] = avatarPreview;
        setExistingAvatarBlob(avatarFile);
      } else if (finalData.avartar && profileData?.id) {
        dispatch(updateAvatarUrl({ id: profileData.id, url: '' }));
        try {
          const newAvatarUrl = await getAvatarUrl(finalData.avartar);
          dispatch(updateAvatarUrl({ id: profileData.id, url: newAvatarUrl }));
          avatarBlobUrls.current[profileData.id] = newAvatarUrl;
          if (avatarToSend) {
            setExistingAvatarBlob(avatarToSend);
          }
        } catch (err) {
          console.error(`Failed to fetch updated avatar for ${profileData.id}:`, err);
          dispatch(updateAvatarUrl({ id: profileData.id, url: '/default-avatar.png' }));
        }
      }
      setIsEditing(false);
      setSnackbarOpen(true);
      setAvatarFile(null);
      setAvatarPreview(null);
      setIsActive(finalData.isActive ?? finalData.status === 'active');
    } catch (err) {
      console.error('Error during submit:', err);
      setSnackbarMessage('Failed to update profile');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const statusMap = {
    active: {
      label: 'Active',
      color: 'success' as const,
      icon: <CheckCircleIcon fontSize="small" />
    },
    inactive: { label: 'Inactive', color: 'error' as const, icon: <Cancel fontSize="small" /> },
    'verify pending': {
      label: 'Verify Pending',
      color: 'warning' as const,
      icon: <InfoIcon fontSize="small" />
    },
    'approval pending': {
      label: 'Approval Pending',
      color: 'warning' as const,
      icon: <InfoIcon fontSize="small" />
    },
    rejected: { label: 'Rejected', color: 'error' as const, icon: <Cancel fontSize="small" /> }
  };

  if (!profileData && !loading) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 3 } }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
          Profile
        </Typography>
        <Typography sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
          No user profile found
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 3 } }}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Typography
        variant="h4"
        sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}
        data-testid="profile-header"
      >
        Profile
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : isEditing ? (
        <DetailCard>
          <CardContent sx={{ p: { xs: 1, sm: 3 } }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6" color="text.primary">
                Edit Profile
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancelEdit}
                disabled={loading}
                startIcon={<Cancel />}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
            </Box>
            <Grid container spacing={{ xs: 1, sm: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                  <CardContent>
                    <Avatar
                      src={
                        avatarPreview ||
                        (profileData?.id
                          ? avatarUrls[profileData.id] || '/default-avatar.png'
                          : '/default-avatar.png')
                      }
                      sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
                      imgProps={{ onError: handleAvatarError }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        component="label"
                        disabled={avatarLoading}
                        sx={{ textTransform: 'none' }}
                      >
                        {avatarLoading ? <CircularProgress size={20} /> : 'Change'}
                        <input type="file" hidden onChange={handleAvatarUpload} accept="image/*" />
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={handleAvatarRemove}
                        disabled={avatarLoading}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: (theme) => `2px dashed ${theme.palette.divider}`,
                        borderRadius: 2,
                        textAlign: 'center',
                        bgcolor: 'background.paper'
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 32, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.primary' }}>
                        Drag & Drop your files here
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        OR
                      </Typography>
                      <Button
                        component="label"
                        variant="outlined"
                        color="primary"
                        startIcon={<CloudUpload />}
                        sx={{ mt: 1, textTransform: 'none' }}
                        disabled={avatarLoading}
                      >
                        {avatarLoading ? <CircularProgress size={20} /> : 'Browse Files'}
                        <input type="file" hidden onChange={handleAvatarUpload} accept="image/*" />
                      </Button>
                      {Capacitor.getPlatform() === 'android' && (
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<CameraAlt />}
                          onClick={takePicture}
                          sx={{ mt: 1, textTransform: 'none' }}
                          disabled={avatarLoading}
                        >
                          Take Photo
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2
                    }}
                  >
                    <Typography variant="h6" color="text.primary">
                      General Information
                    </Typography>
                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value ?? isActive}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                setIsActive(e.target.checked);
                                setValue('status', e.target.checked ? 'active' : 'inactive');
                              }}
                              color="primary"
                              disabled={loading}
                            />
                          }
                          label={field.value ?? isActive ? 'Active' : 'Inactive'}
                          sx={{ color: 'text.secondary' }}
                        />
                      )}
                    />
                  </Box>
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Full Name"
                            variant="outlined"
                            fullWidth
                            size="small"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                            disabled={loading}
                            sx={{ bgcolor: 'background.paper' }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="username"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Username"
                            variant="outlined"
                            fullWidth
                            size="small"
                            error={!!errors.username}
                            helperText={errors.username?.message}
                            disabled={loading}
                            sx={{ bgcolor: 'background.paper' }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Gender
                      </Typography>
                      <Controller
                        name="gender"
                        control={control}
                        render={({ field }) => (
                          <RadioGroup row {...field} sx={{ gap: 2 }}>
                            <FormControlLabel
                              value="male"
                              control={<Radio size="small" />}
                              label="Male"
                              disabled={loading}
                              sx={{ color: 'text.secondary' }}
                            />
                            <FormControlLabel
                              value="female"
                              control={<Radio size="small" />}
                              label="Female"
                              disabled={loading}
                              sx={{ color: 'text.secondary' }}
                            />
                            <FormControlLabel
                              value="other"
                              control={<Radio size="small" />}
                              label="Other"
                              disabled={loading}
                              sx={{ color: 'text.secondary' }}
                            />
                          </RadioGroup>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Phone"
                            variant="outlined"
                            fullWidth
                            size="small"
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                            disabled={loading}
                            sx={{ bgcolor: 'background.paper' }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Email"
                            variant="outlined"
                            fullWidth
                            size="small"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            disabled={loading}
                            sx={{ bgcolor: 'background.paper' }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="dob"
                        control={control}
                        render={({ field }) => (
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Date of Birth"
                              value={field.value}
                              onChange={(date: any) => field.onChange(date)}
                              maxDate={new Date()}
                              slots={{ textField: TextField }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  error: !!errors.dob,
                                  helperText: errors.dob?.message,
                                  disabled: loading,
                                  sx: { bgcolor: 'background.paper' }
                                }
                              }}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="address"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Address (Suburb, State, Country, Postcode)"
                            variant="outlined"
                            fullWidth
                            size="small"
                            error={!!errors.address}
                            helperText={errors.address?.message}
                            disabled={loading}
                            sx={{ bgcolor: 'background.paper' }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="department"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Department"
                            variant="outlined"
                            fullWidth
                            size="small"
                            error={!!errors.department}
                            helperText={errors.department?.message}
                            disabled={loading}
                            sx={{ bgcolor: 'background.paper' }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="role"
                        control={control}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!errors.role}
                            disabled={loading}
                            sx={{ bgcolor: 'background.paper' }}
                          >
                            <InputLabel sx={{ color: 'text.secondary' }}>Role</InputLabel>
                            <Select
                              {...field}
                              label="Role"
                              multiple
                              value={field.value?.map((r) => r.role_id) || []}
                              onChange={(e) => {
                                const value = e.target.value as string[];
                                const selectedRoles = value.map(
                                  (id) =>
                                    roles.find((r) => r.role_id === id) || {
                                      role_id: id,
                                      label: id
                                    }
                                );
                                field.onChange(selectedRoles);
                              }}
                              renderValue={(selected) =>
                                (selected as string[])
                                  .map((id) => roles.find((r) => r.role_id === id)?.label || id)
                                  .join(', ')
                              }
                              sx={{ color: 'text.primary' }}
                            >
                              {roles.map((role) => (
                                <MenuItem key={role.role_id} value={role.role_id}>
                                  {role.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.role && (
                              <Typography color="error" variant="caption">
                                {errors.role.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="groups"
                        control={control}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!errors.groups}
                            disabled={loading}
                            sx={{ bgcolor: 'background.paper' }}
                          >
                            <InputLabel sx={{ color: 'text.secondary' }}>Groups</InputLabel>
                            <Select
                              {...field}
                              multiple
                              label="Groups"
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(
                                  typeof value === 'string' ? value.split(',') : value
                                );
                              }}
                              renderValue={(selected) =>
                                (selected as string[])
                                  .map((id) => groups.find((g) => g.group_id === id)?.label || id)
                                  .join(', ')
                              }
                              sx={{ color: 'text.primary' }}
                            >
                              {groups.map((group) => (
                                <MenuItem key={group.group_id} value={group.group_id}>
                                  {group.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.groups && (
                              <Typography color="error" variant="caption">
                                {errors.groups.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Save'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Grid>
            </Grid>
          </CardContent>
        </DetailCard>
      ) : (
        <DetailCard>
          <CardContent sx={{ p: { xs: 1, sm: 3 } }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6" color="text.primary">
                Profile Details
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Edit />}
                onClick={handleEditClick}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Edit
              </Button>
            </Box>
            <Grid container spacing={{ xs: 1, sm: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
                  <CardContent>
                    <Avatar
                      src={
                        profileData?.id
                          ? avatarUrls[profileData.id] || '/default-avatar.png'
                          : '/default-avatar.png'
                      }
                      sx={{ width: 96, height: 96, mx: 'auto', mb: 2 }}
                      imgProps={{ onError: handleAvatarError }}
                    />
                    <Typography variant="h6" color="text.primary">
                      {profileData?.name || 'N/A'}
                    </Typography>
                    <Chip
                      label={
                        statusMap[profileData?.status as keyof typeof statusMap]?.label || 'Unknown'
                      }
                      size="small"
                      color={
                        statusMap[profileData?.status as keyof typeof statusMap]?.color || 'default'
                      }
                      icon={statusMap[profileData?.status as keyof typeof statusMap]?.icon}
                      sx={{ mt: 1, borderRadius: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <DetailSection>
                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <SectionTitle>
                        <PersonIcon fontSize="small" />
                        PERSONAL INFORMATION
                      </SectionTitle>
                      <DetailItem
                        label="Full Name"
                        value={profileData?.name}
                        icon={<PersonIcon fontSize="small" />}
                      />
                      <DetailItem
                        label="Username"
                        value={profileData?.username}
                        icon={<BadgeIcon fontSize="small" />}
                      />
                      <DetailItem
                        label="Email"
                        value={profileData?.email}
                        icon={<EmailIcon fontSize="small" />}
                      />
                      <DetailItem
                        label="Phone"
                        value={profileData?.phone}
                        icon={<PhoneIcon fontSize="small" />}
                      />
                      <DetailItem
                        label="Gender"
                        value={profileData?.gender}
                        icon={<TransgenderIcon fontSize="small" />}
                      />
                      <DetailItem
                        label="Date of Birth"
                        value={
                          profileData?.dob
                            ? new Date(profileData.dob).toLocaleDateString()
                            : undefined
                        }
                        icon={<CakeIcon fontSize="small" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SectionTitle>
                        <HomeIcon fontSize="small" />
                        ADDRESS
                      </SectionTitle>
                      <DetailItem
                        label="Location"
                        value={profileData?.address}
                        multiline
                        icon={<HomeIcon fontSize="small" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SectionTitle>
                        <WorkIcon fontSize="small" />
                        PROFESSIONAL INFORMATION
                      </SectionTitle>
                      <DetailItem
                        label="Department"
                        value={profileData?.department}
                        icon={<WorkIcon fontSize="small" />}
                      />
                      <DetailItem
                        label="Role"
                        value={
                          profileData?.role && profileData.role.length > 0
                            ? profileData.role.map((r) => r.label).join(', ')
                            : undefined
                        }
                        icon={<BadgeIcon fontSize="small" />}
                      />
                      <DetailItem
                        label="Groups"
                        value={(profileData?.groups || [])
                          .map((groupId) => groups.find((g) => g.group_id === groupId)?.label)
                          .filter(Boolean)
                          .join(', ')}
                        multiline
                        icon={<GroupsIcon fontSize="small" />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SectionTitle>
                        <InfoIcon fontSize="small" />
                        SYSTEM INFORMATION
                      </SectionTitle>
                      <DetailItem label="Status" icon={<InfoIcon fontSize="small" />}>
                        <Chip
                          label={
                            statusMap[profileData?.status as keyof typeof statusMap]?.label ||
                            'Unknown'
                          }
                          size="small"
                          color={
                            statusMap[profileData?.status as keyof typeof statusMap]?.color ||
                            'default'
                          }
                          icon={statusMap[profileData?.status as keyof typeof statusMap]?.icon}
                          sx={{ borderRadius: 1 }}
                        />
                      </DetailItem>
                      <DetailItem
                        label="Created By"
                        value={profileData?.created_by}
                        icon={<PersonAddIcon fontSize="small" />}
                      />
                      <DetailItem
                        label="Creation Date"
                        value={
                          profileData?.creation_date_time
                            ? new Date(profileData.creation_date_time).toLocaleString()
                            : undefined
                        }
                        icon={<CalendarIcon fontSize="small" />}
                      />
                    </Grid>
                  </Grid>
                </DetailSection>
              </Grid>
            </Grid>
          </CardContent>
        </DetailCard>
      )}
      {error && (
        <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
          {error}
        </Typography>
      )}
    </Container>
  );
};

export default ProfileContent;
